import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPriceShape, formatCHF } from '../../utils/priceUtils';

/**
 * Canonical price renderer for the shop.
 *
 * Pass it any article-like input (article, product, cart item, scalar, price
 * object) and it renders: current price, optional strike-through base price,
 * optional discount-percentage badge.
 *
 * Variants:
 *   <PriceDisplay article={a} />                             default product-card style
 *   <PriceDisplay article={a} variant="hero" />              big text (product-detail page)
 *   <PriceDisplay article={a} variant="compact" />           small (search dropdown)
 *   <PriceDisplay article={a} variant="card" />              listing cards: compact on phones, default from sm
 *   <PriceDisplay article={a} fallbackKey="..." />           custom i18n key for missing price
 *   <PriceDisplay article={a} showVatHint />                 add "incl. VAT" line
 */
const VARIANTS = {
  default: {
    current: 'text-lg font-bold text-gray-900',
    base: 'text-sm text-gray-500 line-through',
    badge: 'text-xs font-semibold text-white bg-sale-soft px-2 py-0.5 rounded-full',
    fallback: 'text-sm font-medium text-gray-500',
  },
  hero: {
    current: 'text-3xl font-bold text-gray-900',
    base: 'text-lg text-gray-500 line-through',
    badge: 'text-sm font-semibold text-white bg-sale-soft px-2.5 py-1 rounded-full',
    fallback: 'text-base font-medium text-gray-500',
  },
  card: {
    current: 'text-[15px] font-extrabold tracking-tight text-gray-900 sm:text-lg',
    base: 'text-[11px] text-gray-400 line-through sm:text-sm',
    badge: 'text-[10px] font-semibold text-white bg-sale-soft px-1.5 py-0.5 rounded-full sm:text-xs sm:px-2',
    fallback: 'text-xs font-medium text-gray-500 sm:text-sm',
  },
  compact: {
    current: 'text-sm font-semibold text-gray-900',
    base: 'text-xs text-gray-500 line-through',
    badge: 'text-[10px] font-semibold text-white bg-sale-soft px-1.5 py-0.5 rounded-full',
    fallback: 'text-xs font-medium text-gray-500',
  },
};

const PriceDisplay = ({
  article,
  variant = 'default',
  fallbackKey = 'products.card.priceNotAvailable',
  showVatHint = false,
  showBadge = true,
  className = '',
}) => {
  const { t } = useLanguage();
  const styles = VARIANTS[variant] || VARIANTS.default;
  const p = getPriceShape(article);

  if (p.currentPrice == null) {
    return <p className={`${styles.fallback} ${className}`}>{t(fallbackKey)}</p>;
  }

  // The VAT hint sits on its own line, always. It used to be the last child of
  // the wrapping flex row with `ml-auto`, which meant it rode alongside the
  // price on wide cards and dropped below it on narrow ones (or whenever a
  // strike-through price and discount badge were present) — so the same grid
  // showed both layouts at once. Its own block makes every card agree.
  return (
    <div className={className}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className={styles.current}>
          {p.currency} {formatCHF(p.currentPrice)}
        </span>
        {p.inDiscount && p.basePrice != null && (
          <>
            <span className={styles.base}>
              {p.currency} {formatCHF(p.basePrice)}
            </span>
            {showBadge && p.discountPercentage != null && (
              <span className={styles.badge}>-{p.discountPercentage}%</span>
            )}
          </>
        )}
      </div>
      {showVatHint && (
        <span className="mt-1 block text-xs text-gray-500">
          {t('products.card.includingVat')}
        </span>
      )}
    </div>
  );
};

export default PriceDisplay;
